import { JsonBlock } from "@/components/CodeBlock";
import { ByteView, Field, FieldGroup } from "@/components/HashDisplay";
import { Prop } from "@/components/Prop";
import { toHex } from "@/hashtree/bytes";
import { decodeNode } from "@/hashtree/msgpack";
import { type BuiltNode } from "@/hashtree/nodes";
import type { HashtreeBuild } from "@/hashtree/useHashtreeBuild";
import { Slide, SpecNote, Step } from "./Slide";

function decodedForm(node: BuiltNode) {
  const parsed = decodeNode(node.plaintext);
  return {
    l: parsed.l.map((link) => ({
      h: toHex(link.h),
      ...(link.k ? { k: toHex(link.k) } : {}),
      ...(link.m ? { m: link.m } : {}),
      ...(link.n !== undefined ? { n: link.n } : {}),
      s: link.s,
      t: link.t,
    })),
    t: parsed.t,
  };
}

const LINK_TYPES: Record<number, string> = {
  0: "raw blob",
  1: "file manifest",
  2: "directory manifest",
  3: "directory fanout",
};

/** Slide 5: the folder holding the file. */
export function FolderSlide({ build, onNext }: { build: HashtreeBuild; onNext: () => void }) {
  const { tree, file, folderName, setFolderName, folderNameError } = build;
  if (!file) return null;

  // The name field must render no matter what. Bailing out early when the tree
  // is missing used to take the input away the moment the name became invalid,
  // leaving no way to type a valid one.
  const folder = tree?.folder;
  const link = folder?.links[0];

  return (
    <Slide
      bud={16}
      title="Wrap it in a folder"
      lede={
        <>
          A directory manifest is a sorted list of named links. This is the node type that turns loose blobs into
          something you can walk with a path.
        </>
      }
    >
      <div className="space-y-1.5">
        <label htmlFor="folder-name" className="text-sm font-medium">
          Folder name
        </label>
        <input
          id="folder-name"
          value={folderName}
          onChange={(event) => setFolderName(event.target.value)}
          className="w-full max-w-sm rounded-md border bg-background px-3 py-1.5 font-mono text-sm"
        />
        {folderNameError && (
          <p className="text-xs text-red-600 dark:text-red-400">
            {folderNameError}. The tree below still shows the last valid name.
          </p>
        )}
      </div>

      <SpecNote>
        Entry names must be valid UTF-8, non-empty, unique within their directory, and must not contain <code>/</code>{" "}
        or NUL, or be <code>.</code> or <code>..</code>. Paths are resolved by splitting on <code>/</code> and matching
        each segment against a link's <Prop name="n" />.
      </SpecNote>

      <Step n={1} title="One named link, pointing at your file">
        {link && (
          <FieldGroup>
            <Field label={<Prop name="n" />} value={link.n ?? ""} hint="The directory entry name" />
            <Field label={<Prop name="h" />} value={toHex(link.h)} tone="hash" hint="Blossom hash of the linked object" />
            <Field
              label={<Prop name="k" />}
              value={link.k ? toHex(link.k) : "—"}
              tone="key"
              hint="The child's CHK key, carried by the parent. This is how one root key opens the whole tree."
            />
            <Field label={<Prop name="s" />} value={`${link.s} bytes of plaintext`} copyable={false} />
            <Field
              label={<Prop name="t" value={link.t} />}
              value={LINK_TYPES[link.t] ?? "unknown type"}
              copyable={false}
            />
          </FieldGroup>
        )}
      </Step>

      <Step n={2} title="Encoded, then encrypted">
        {folder && (
          <>
            <JsonBlock value={decodedForm(folder)} caption="decoded t=2 node" />
            <ByteView bytes={folder.plaintext} label="MessagePack bytes" />
            <FieldGroup>
              <Field label="blob hash" value={toHex(folder.encrypted.blobHash)} tone="hash" />
              <Field label="chk key" value={toHex(folder.encrypted.chkKey)} tone="key" />
            </FieldGroup>
          </>
        )}
      </Step>

      <SpecNote tone="warn" title="A public manifest leaks everything in it">
        Entry names, sizes, child hashes and every embedded <Prop name="k" /> are visible to anyone who can read this
        manifest. Encrypting the manifest — which is what we just did — is what keeps them private.
      </SpecNote>

      <button
        type="button"
        onClick={onNext}
        disabled={Boolean(folderNameError) || !folder}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
      >
        Build the root
      </button>
    </Slide>
  );
}

/** Slide 6: the root directory. */
export function RootSlide({ build, onNext }: { build: HashtreeBuild; onNext: () => void }) {
  const { tree, folderName } = build;
  if (!tree) return null;

  const root = tree.root;
  const link = root.links[0];

  return (
    <Slide
      bud={16}
      title="The root"
      lede={
        <>
          The root is just another <Prop name="t" value={2} /> directory. Nothing about it is special in the format — it becomes
          the root only because something points at it, which is what the next steps do.
        </>
      }
    >
      {link && (
        <FieldGroup>
          <Field label={<Prop name="n" />} value={link.n ?? folderName} />
          <Field label={<Prop name="h" />} value={toHex(link.h)} tone="hash" hint="Your folder's blob hash" />
          <Field label={<Prop name="k" />} value={link.k ? toHex(link.k) : "—"} tone="key" hint="Your folder's key" />
          <Field label={<Prop name="s" />} value={`${link.s} bytes of descendant file data`} copyable={false} />
          <Field
            label={<Prop name="t" value={link.t} />}
            value={LINK_TYPES[link.t] ?? "unknown type"}
            copyable={false}
          />
        </FieldGroup>
      )}

      <JsonBlock value={decodedForm(root)} caption="decoded t=2 root node" />
      <ByteView bytes={root.plaintext} label="MessagePack bytes" />

      <div className="space-y-2 rounded-lg border-2 border-primary/40 bg-primary/5 p-4">
        <p className="text-sm font-medium">These two values are the entire tree</p>
        <FieldGroup className="border-primary/20 bg-background/60">
          <Field
            label="root hash"
            value={toHex(root.encrypted.blobHash)}
            tone="hash"
            hint="Fetch this blob and you can walk down to every other node"
          />
          <Field
            label="root key"
            value={toHex(root.encrypted.chkKey)}
            tone="key"
            hint="Decrypts the root, which reveals the child keys, which reveal theirs, and so on"
          />
        </FieldGroup>
        <p className="text-xs text-muted-foreground">
          {tree.blobs.length} blobs in total. Everything else in the tree is reachable from these.
        </p>
      </div>

      <button
        type="button"
        onClick={onNext}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
      >
        Upload the blobs
      </button>
    </Slide>
  );
}
